import {
    ComponentManager,
    GlobalConfiguration as Config, NavigationHelper,
    RADApplication,
    ResourceManager,
    UIInterceptor
} from 'radpresentation.htmlclient.defaultviewextensions';
import { INavigationNodeDTO } from 'coreframetypings/dto';

export default class ONVUIInterceptor extends UIInterceptor
{
    /** Rollen, die den Kursunterlagen-Download in der Navigation sehen duerfen. */
    private static readonly KURSUNTERLAGEN_DOWNLOAD_ROLES: string[] = ['Referent', 'LVReferent'];

    /** Rollen, die die Benutzerlisten (ONV-Benutzer und erweiterte CF-Benutzer) sehen duerfen. */
    private static readonly BENUTZER_LV_ROLES: string[] = ['LVReferent', 'DVAdmin'];

    /** Name des ONV-Benutzer-LV-Knotens. */
    private static readonly BENUTZER_LV_NODE: string = 'ONV.ONV.Benutzer_LV';

    /** Name des CF-Benutzer-LV-Knotens (Framework-User, "erweiterte" Ansicht). */
    private static readonly CF_USER_NODE: string = 'RAD.RADFoundation.ProblemDomain.User_LV';
    
    /** Reihenfolge der Root-Items */
    private static readonly NAVIGATION_NODE_SORT_ORDER: string[] = [
        'ONV.ONV.Newcomer_LV', 'ONV.ONV.Kurs_LV', 'statistic', 'kursunterlagen', 'benutzer_uebersicht', ];
    
    public afterStartup(callback: () => void): void {
        const config: any = Config;
        
        config.headerView.showDarkModeSwitch = false;
        
        if (config && config.inlineImages)
        {
            config.inlineImages.COMPANY_HEADER_LOGO_SMALL = 'ovsv_text_invert.svg';
        }
        
        callback();
    }

    public beforeLoadNavigationTree(rootNode: INavigationNodeDTO): void {
        const onvModuleNode = rootNode.childNodes[0];

        if (this.isEligible(ONVUIInterceptor.KURSUNTERLAGEN_DOWNLOAD_ROLES))
            this.addKursunterlagenDownloadNode(onvModuleNode);

        this.addStatisticNode(onvModuleNode);
    
        this.updateBenutzerUebersichtNode(onvModuleNode);
        this.updateKursunterlagen(onvModuleNode);
        
        this.sortChildNodes(onvModuleNode, ONVUIInterceptor.NAVIGATION_NODE_SORT_ORDER);
        this.expandAll(rootNode);
    }

    public beforeLoadMenuTree(rootNode: INavigationNodeDTO): void {
        this.applyUserNodeVisibility(rootNode);
    }

    /**
     * Steuert die Sichtbarkeit der beiden Benutzerlisten (nur fuer bestimmte Rollen bzw. SuperUser):
     * - ONV-Benutzer: fuer berechtigte Rollen sichtbar (Label unveraendert), sonst entfernt.
     * - CF-Benutzer (erweiterte Ansicht): fuer berechtigte Rollen sichtbar mit Postfix "(Erweitert)",
     *   sonst entfernt.
     */
    private applyUserNodeVisibility(root: INavigationNodeDTO): void {
        const eligible = this.isEligible(ONVUIInterceptor.BENUTZER_LV_ROLES);

        if (!eligible)
            this.removeNode(root, ONVUIInterceptor.BENUTZER_LV_NODE);

        if (eligible)
            this.addErweitertPostfix(root, ONVUIInterceptor.CF_USER_NODE);
        else
            this.removeNode(root, ONVUIInterceptor.CF_USER_NODE);
    }
    
    private addErweitertPostfix(root: INavigationNodeDTO, nodeName: string): void {
        const node = this.findNode(root, nodeName);
        if (!node)
            return;

        const baseLabel = ResourceManager.getInstance().get(node.ergName);
        node.caption = `${baseLabel} (Erweitert)`;
        node.ergName = null;
    }

    private updateKursunterlagen(onvModuleNode: INavigationNodeDTO) {
        this.addKursunterlagenNode(onvModuleNode);
        
        const kursunterlagenNodes = [
            'ONV.ONV.Bestellung_LV',
            'kursunterlagen_download',
            'ONV.ONV.Datei_LV'
        ];
        for (const nodeName of kursunterlagenNodes) {
            this.moveNodeInto(onvModuleNode, nodeName, 'kursunterlagen');
        }

        this.removeNode(onvModuleNode, 'kursunterlagen', true);
    }

    private updateBenutzerUebersichtNode(onvModuleNode: INavigationNodeDTO) {
        this.addBenutzerUebersichtNode(onvModuleNode);

        const benutzterNodes = [
            'ONV.ONV.Benutzer_LV',
            'ONV_LV_LEITUNG_NODE',
            'ONV_LV_REFERENTEN_NODE'
        ];
        for (const nodeName of benutzterNodes) {
            this.moveNodeInto(onvModuleNode, nodeName, 'benutzer_uebersicht');
        }

        this.applyUserNodeVisibility(onvModuleNode);

        this.removeNode(onvModuleNode, 'benutzer_uebersicht', true);
    }
     
    private addBenutzerUebersichtNode(onvModuleNode: INavigationNodeDTO): void {
        onvModuleNode.childNodes.push({
            name: 'benutzer_uebersicht',
            ergName: 'ONV.ONV.NAVIGATION_BENUTZERUEBERSICHT_ERGNAME',
            action: null,
            viewExtensionFactory: null,
            isListView: false,
            viewPDOID: '-1',
            childNodes: [],
            iconUrl: 'Icons/graduate.svg',
            pdTypeName: null,
            tooltip: null,
            url: null
        });
    }

    private addKursunterlagenNode(onvModuleNode: INavigationNodeDTO): void {
        onvModuleNode.childNodes.push({
            name: 'kursunterlagen',
            ergName: 'ONV.ONV.NAVIGATION_KURSUNTERLAGEN_ERGNAME',
            action: null,
            viewExtensionFactory: null,
            isListView: false,
            viewPDOID: '-1',
            childNodes: [],
            iconUrl: 'Icons/cabinet.svg',
            pdTypeName: null,
            tooltip: null,
            url: null
        });
    }

    private addStatisticNode(onvModuleNode: INavigationNodeDTO) {
        onvModuleNode.childNodes.push({
            name: 'statistic',
            ergName: 'ONV.ONV.NAVIGATION_STATISTIC_ERGNAME',
            viewExtensionFactory: 'ONVStatistic',
            isListView: false,
            viewPDOID: '-1',
            childNodes: [],
            iconUrl: "Icons/chart_column.svg",
            action: null,
            pdTypeName: null,
            tooltip: null,
            url: null
        });
    }

    private addKursunterlagenDownloadNode(onvModuleNode: INavigationNodeDTO) {

        onvModuleNode.childNodes.push({
            name: 'kursunterlagen_download',
            ergName: 'ONV.ONV.NAVIGATION_KURSUNTERLAGEN_DOWNLOAD_ERGNAME',
            action: "CUSTOM_ACTION",
            actionCallback: () => {
                NavigationHelper.openDetailView('EDIT', "ONV.ONV.KursunterlagenGenerator")
            },
            isListView: false,
            viewExtensionFactory: null,
            viewPDOID: '-1',
            childNodes: [],
            iconUrl: "Icons/cloud_download.svg",
            pdTypeName: null,
            tooltip: null,
            url: null,
        });
    }

    /**
     * Verschiebt den Knoten mit dem Namen <paramref name="sourceName"/> als Kindknoten in den
     * Knoten mit dem Namen <paramref name="targetName"/>. Fehlt einer der beiden Knoten im Baum,
     * passiert nichts.
     */
    private moveNodeInto(root: INavigationNodeDTO, sourceName: string, targetName: string): void {
        const sourceParent = this.findParentOf(root, sourceName);
        const targetNode = this.findNode(root, targetName);
        if (!sourceParent || !targetNode)
        {
            return;
        }

        const index = sourceParent.childNodes.findIndex(node => node.name === sourceName);
        const [sourceNode] = sourceParent.childNodes.splice(index, 1);
        targetNode.childNodes.push(sourceNode);
    }

    /**
     * Entfernt den Knoten mit dem angegebenen Namen aus dem Baum. Ist <paramref name="onlyIfEmpty"/>
     * gesetzt, wird der Knoten nur entfernt, wenn er keine Kindknoten besitzt. Fehlt der Knoten,
     * passiert nichts.
     */
    private removeNode(root: INavigationNodeDTO, name: string, onlyIfEmpty: boolean = false): void {
        const parent = this.findParentOf(root, name);
        if (!parent)
            return;

        const index = parent.childNodes.findIndex(child => child.name === name);
        if (index === -1)
            return;

        if (onlyIfEmpty && parent.childNodes[index].childNodes.length > 0)
            return;

        parent.childNodes.splice(index, 1);
    }

    /**
     * Sucht rekursiv den Knoten mit dem angegebenen Namen (inkl. des Startknotens selbst).
     */
    private findNode(node: INavigationNodeDTO, name: string): INavigationNodeDTO | null {
        if (node.name === name)
        {
            return node;
        }

        for (const child of node.childNodes)
        {
            const found = this.findNode(child, name);
            if (found)
            {
                return found;
            }
        }

        return null;
    }

    /**
     * Sucht rekursiv den Elternknoten, dessen direkter Kindknoten den angegebenen Namen trägt.
     */
    private findParentOf(node: INavigationNodeDTO, childName: string): INavigationNodeDTO | null {
        if (node.childNodes.some(child => child.name === childName))
        {
            return node;
        }

        for (const child of node.childNodes)
        {
            const found = this.findParentOf(child, childName);
            if (found)
            {
                return found;
            }
        }

        return null;
    }

    /**
     * Sortiert die direkten Kindknoten von <paramref name="parent"/> gemäß der Reihenfolge der
     * Namen in <paramref name="order"/>. Namen aus <paramref name="order"/>, zu denen es keinen
     * Kindknoten gibt, werden ignoriert. Kindknoten, die nicht in <paramref name="order"/> stehen,
     * behalten ihre ursprüngliche Reihenfolge und werden hinter den sortierten Knoten angehängt.
     */
    private sortChildNodes(parent: INavigationNodeDTO, order: string[]): void {
        if (order.length === 0)
        {
            return;
        }

        const rank = (name: string): number =>
        {
            const index = order.indexOf(name);
            return index === -1 ? order.length : index;
        };

        parent.childNodes.sort((a, b) => rank(a.name) - rank(b.name));
    }

    /**
     * Klappt den angegebenen Knoten und rekursiv alle Kindknoten auf.
     */
    private expandAll(node: INavigationNodeDTO): void {
        (node as any).isExpanded = true;

        for (const child of node.childNodes)
        {
            this.expandAll(child);
        }
    }

    private isEligible(relevantRoles: string[]) : boolean {
        const user = RADApplication.getInstance().user;
        if (!user)
            return false;

        if (user.isSuperUser())
            return true;
        
        const roles = user.roles ?? [];
        return roles.some(role => relevantRoles.includes(role));
    }
}

ComponentManager.setUIInterceptor('ONVUIInterceptor', ONVUIInterceptor, 'ONVHTMLViewExtension');
